// ============================================================================
// useFinanceStats.js — V7 ULTRA SPARKLINES (CÁLCULOS + MINI GRÁFICOS DIÁRIOS)
// ============================================================================
// 🔥 V7: computeDailySparkline — arrays de 6 valores diarios para cada métrica
// 🔥 V7: sparklinesReceitas, sparklinesDespesas, sparklinesVendas, etc.
// 🔥 V7: Agrupamento por día (YYYY-MM-DD) con preenchimento de gaps
// 🔥 V7: Protección total contra días vacíos, NaN, Infinity
// 🔥 V7: Ordenación cronológica estricta (más reciente primero → slice)
// 🔥 V7: Compatible 100% con SummaryCards V3 (props sparkline ya existentes)
// 🔥 V6: safeDivide() en ticketMedio — NUNCA NaN
// 🔥 V6: safeNum() en extractValue para totalMesAnterior
// 🔥 V6: isFinite() antes de Math.round() en percentual
// 🔥 V6: statsPorCategoria con fallback de type
// 🔥 V6: tendencia con protección total contra NaN/Infinity
// 🔥 V6: resumoMensal con ticketMedio ultra preciso
// 🔥 V6: totaisGerais con normalización de type
// ✅ Normalización de type (receita/despesa, income/expense, entrada/saída)
// ✅ Normalización de value/valor (aceita value, valor, amount, price)
// ✅ Protección contra NaN, Infinity, undefined
// ✅ Deduplicación segura (IDs ausentes generan fallback)
// ✅ statsPorCategoria ordenado por total (mayor primero)
// ✅ Nivel Enterprise: Airbnb/Zillow/QuintoAndar · Vercel Analytics · Stripe
// ============================================================================

import { useMemo } from "react";

// ── Helpers puros (sin dependencias externas) ──────────────────────────────

const safe = (arr) => (Array.isArray(arr) ? arr : []);

/**
 * 🔥 V6: safeNum — NUNCA devuelve NaN o Infinity
 */
const safeNum = (value, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? fallback : num;
};

/**
 * 🔥 V6: safeDivide — división ultra precisa, NUNCA NaN
 */
const safeDivide = (numerator, denominator, fallback = 0) => {
  const num = safeNum(numerator, 0);
  const den = safeNum(denominator, 0);
  if (den === 0) return fallback;
  const result = num / den;
  return isFinite(result) ? result : fallback;
};

/**
 * 🔥 V6: safePercent — porcentaje seguro, siempre 0-100
 */
const safePercent = (value) => {
  const num = safeNum(value, 0);
  return Math.min(100, Math.max(0, Math.round(num * 10) / 10));
};

/**
 * Normaliza o type do registro financeiro.
 * Aceita: receita, despesa, income, expense, entrada, saída, crédito, débito
 */
const normalizeType = (type) => {
  if (!type) return null;
  const t = String(type).toLowerCase().trim();
  
  if (['receita', 'income', 'entrada', 'crédito', 'credito'].includes(t)) return 'receita';
  if (['despesa', 'expense', 'saída', 'saida', 'débito', 'debito'].includes(t)) return 'despesa';
  if (t === 'receita' || t === 'despesa') return t;
  
  return t;
};

/**
 * Extrai valor numérico seguro do registro.
 * Aceita: value, valor, amount, price
 */
const extractValue = (item) => {
  if (!item) return 0;
  
  const raw = item.value ?? item.valor ?? item.amount ?? item.price ?? 0;
  
  if (typeof raw === 'number') return isFinite(raw) ? raw : 0;
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isFinite(parsed) ? parsed : 0;
  }
  
  return 0;
};

/**
 * 🔥 V8: Acepta YYYY-MM-DD, ISO (…T…), timestamps y Date
 * Evita perder registros con createdAt ISO → cálculos ultra precisos
 */
const extractYearMonth = (dateStr) => {
  if (dateStr == null || dateStr === '') return null;
  if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
    return `${dateStr.getFullYear()}-${String(dateStr.getMonth() + 1).padStart(2, '0')}`;
  }
  const s = String(dateStr);
  const match = s.match(/^(\d{4})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}`;
  const t = Date.parse(s);
  if (!isNaN(t)) {
    const d = new Date(t);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
  return null;
};

/**
 * 🔥 V8: Día YYYY-MM-DD desde cualquier formato de fecha válido
 */
const extractDay = (dateStr) => {
  if (dateStr == null || dateStr === '') return null;
  if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
    return `${dateStr.getFullYear()}-${String(dateStr.getMonth() + 1).padStart(2, '0')}-${String(dateStr.getDate()).padStart(2, '0')}`;
  }
  const s = String(dateStr);
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const t = Date.parse(s);
  if (!isNaN(t)) {
    const d = new Date(t);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return null;
};

/**
 * Gera um ID único para deduplicação quando o registro não tem ID
 */
let _fallbackIdCounter = 0;
const getItemId = (item, index) => {
  if (item?.id) return String(item.id);
  if (item?.uid) return String(item.uid);
  if (item?._localId) return String(item._localId);
  const contentKey = `${item?.category || ''}_${item?.date || ''}_${item?.title || ''}_${extractValue(item)}`;
  if (contentKey.length > 3) return `auto_${contentKey}`;
  return `fallback_${_fallbackIdCounter++}_${index}`;
};

// ============================================================================
// 🆕 V7: SPARKLINE ENGINE — Mini gráficos diarios estilo Vercel Analytics
// ============================================================================

/**
 * 🔥 V7: Agrupa registros por día y devuelve array de valores diarios.
 * @param {Array} items - Registros financieros
 * @param {Function} typeFilter - Filtro opcional por tipo (receita/despesa)
 * @param {string} mes - Mes actual (YYYY-MM) para limitar el rango
 * @param {number} days - Número de días a devolver (default: 6)
 * @returns {Array<number>} Array de valores diarios (longitud = days)
 */
const computeDailySparkline = (items, typeFilter, mes, days = 6) => {
  const registros = safe(items);
  
  // ── 1. Agrupar por día ─────────────────────────────────────────────────
  const dailyMap = {};
  
  registros.forEach((item) => {
    // Aplicar filtro de tipo si existe
    if (typeFilter && normalizeType(item.type) !== typeFilter) return;
    
    const day = extractDay(item?.date || item?.createdAt || item?.data);
    if (!day) return;
    
    // Opcional: limitar al mes actual para sparklines de resumen mensual
    if (mes && extractYearMonth(item?.date) !== mes) return;
    
    dailyMap[day] = (dailyMap[day] || 0) + extractValue(item);
  });
  
  // ── 2. Ordenar cronológicamente y tomar últimos N días ─────────────────
  const sortedDays = Object.keys(dailyMap).sort();
  
  if (sortedDays.length === 0) {
    // Sin datos: devolver array de ceros para mantener estructura
    return Array(days).fill(0);
  }
  
  // Tomar los últimos 'days' días con datos
  const recentDays = sortedDays.slice(-days);
  const result = recentDays.map((day) => safeNum(dailyMap[day], 0));
  
  // ── 3. Rellenar si hay menos días que 'days' ───────────────────────────
  while (result.length < days) {
    result.unshift(0); // Añadir ceros al inicio (días sin datos)
  }
  
  return result;
};

/**
 * 🔥 V7: Sparkline para Vendas (filtra por categoría de venta)
 */
const computeVendasSparkline = (items, mes, days = 6) => {
  const registros = safe(items);
  
  const dailyMap = {};
  
  registros.forEach((item) => {
    const cat = (item.category || '').toLowerCase().trim();
    const isVenda = cat === 'venda de imóvel' || cat === 'venda de imovel' || 
                    cat === 'venda' || cat === 'sales';
    if (!isVenda) return;
    
    const day = extractDay(item?.date);
    if (!day) return;
    if (mes && extractYearMonth(item?.date) !== mes) return;
    
    dailyMap[day] = (dailyMap[day] || 0) + extractValue(item);
  });
  
  const sortedDays = Object.keys(dailyMap).sort();
  
  if (sortedDays.length === 0) return Array(days).fill(0);
  
  const recentDays = sortedDays.slice(-days);
  const result = recentDays.map((day) => safeNum(dailyMap[day], 0));
  
  while (result.length < days) {
    result.unshift(0);
  }
  
  return result;
};

/**
 * 🔥 V7: Sparkline para Aluguéis (filtra por categoría de aluguel)
 */
const computeAlugueisSparkline = (items, mes, days = 6) => {
  const registros = safe(items);
  
  const dailyMap = {};
  
  registros.forEach((item) => {
    const cat = (item.category || '').toLowerCase().trim();
    const isAluguel = cat === 'aluguel' || cat === 'aluguéis' || 
                      cat === 'alugueis' || cat === 'rent' || cat === 'rental';
    if (!isAluguel) return;
    
    const day = extractDay(item?.date);
    if (!day) return;
    if (mes && extractYearMonth(item?.date) !== mes) return;
    
    dailyMap[day] = (dailyMap[day] || 0) + extractValue(item);
  });
  
  const sortedDays = Object.keys(dailyMap).sort();
  
  if (sortedDays.length === 0) return Array(days).fill(0);
  
  const recentDays = sortedDays.slice(-days);
  const result = recentDays.map((day) => safeNum(dailyMap[day], 0));
  
  while (result.length < days) {
    result.unshift(0);
  }
  
  return result;
};

// ============================================================================
// 🎯 HOOK PRINCIPAL — V7 ULTRA SPARKLINES
// ============================================================================
export const useFinanceStats = (registros, mes, filtroTipo) => {
  const r = safe(registros);

  // ── Tem registros no mês ──────────────────────────────────────────────────
  const temRegistrosNoMes = useMemo(
    () => r.some((item) => extractYearMonth(item?.date || item?.createdAt || item?.data) === mes),
    [r, mes]
  );

  // ── Registros filtrados (com deduplicação segura) ─────────────────────────
  const registrosFiltrados = useMemo(() => {
    // 🔥 V8: filtrar por mes estricto — NUNCA mezclar otros meses si el mes no tiene datos
    // (antes caía al total histórico y confundía totales del header)
    const porMes = r.filter((item) => {
      const ym = extractYearMonth(item?.date || item?.createdAt || item?.data);
      return ym === mes;
    });
    const base = porMes;

    let resultado;
    const tipoNormalizado = String(filtroTipo || 'todos').toLowerCase().trim();

    switch (tipoNormalizado) {
      case 'todos':
        resultado = base;
        break;
      case 'vendas':
      case 'venda':
      case 'venda de imóvel':
      case 'venda de imovel':
        resultado = base.filter((x) => {
          const cat = (x.category || '').toLowerCase().trim();
          return cat === 'venda de imóvel' || cat === 'venda de imovel' || 
                 cat === 'venda' || cat === 'sales';
        });
        break;
      case 'alugueis':
      case 'aluguel':
      case 'aluguéis':
      case 'rent':
        resultado = base.filter((x) => {
          const cat = (x.category || '').toLowerCase().trim();
          return cat === 'aluguel' || cat === 'aluguéis' || 
                 cat === 'alugueis' || cat === 'rent' || cat === 'rental';
        });
        break;
      case 'receita':
      case 'receitas':
      case 'income':
        resultado = base.filter((x) => normalizeType(x.type) === 'receita');
        break;
      case 'despesa':
      case 'despesas':
      case 'expense':
        resultado = base.filter((x) => normalizeType(x.type) === 'despesa');
        break;
      default:
        resultado = base.filter((x) => 
          String(x.type || '').toLowerCase().trim() === tipoNormalizado
        );
        if (resultado.length === 0 && base.length > 0) {
          resultado = base;
        }
    }

    // Deduplicação segura
    const seen = new Map();
    resultado.forEach((item, index) => {
      const id = getItemId(item, index);
      if (!seen.has(id)) {
        seen.set(id, item);
      }
    });

    return Array.from(seen.values());
  }, [r, mes, filtroTipo]);

  // ── Stats de vendas ───────────────────────────────────────────────────────
  const vendasStats = useMemo(() => {
    const vendas = r.filter((x) => {
      const cat = (x.category || '').toLowerCase().trim();
      return cat === 'venda de imóvel' || cat === 'venda de imovel' || 
             cat === 'venda' || cat === 'sales';
    });
    
    const vendasMes = vendas.filter((v) => extractYearMonth(v?.date || v?.createdAt) === mes);
    
    return {
      total:        vendas.length,
      totalValor:   vendas.reduce((acc, v) => acc + extractValue(v), 0),
      mes:          vendasMes.length,
      mesValor:     vendasMes.reduce((acc, v) => acc + extractValue(v), 0),
      propriedades: new Set(vendas.map((v) => v.propertyId).filter(Boolean)).size,
    };
  }, [r, mes]);

  // ── Stats de aluguéis ─────────────────────────────────────────────────────
  const alugueisStats = useMemo(() => {
    const alugueis = r.filter((x) => {
      const cat = (x.category || '').toLowerCase().trim();
      return cat === 'aluguel' || cat === 'aluguéis' || 
             cat === 'alugueis' || cat === 'rent' || cat === 'rental';
    });
    
    const alugueisAtivos = alugueis.filter((a) => {
      const status = (a.status || '').toLowerCase().trim();
      return status === 'active' || status === 'ativo' || status === 'em_andamento';
    });
    
    const alugueisMes = alugueis.filter((a) => extractYearMonth(a?.date || a?.createdAt) === mes);
    
    return {
      total:        alugueis.length,
      ativos:       alugueisAtivos.length,
      totalValor:   alugueis.reduce((acc, a) => acc + extractValue(a), 0),
      mes:          alugueisMes.length,
      mesValor:     alugueisMes.reduce((acc, a) => acc + extractValue(a), 0),
      propriedades: new Set(alugueis.map((a) => a.propertyId).filter(Boolean)).size,
    };
  }, [r, mes]);

  // ── Totais (com normalização de type) ─────────────────────────────────────
  const totalReceitas = useMemo(
    () => safe(registrosFiltrados)
      .filter((x) => normalizeType(x.type) === 'receita')
      .reduce((acc, x) => acc + extractValue(x), 0),
    [registrosFiltrados]
  );

  const totalDespesas = useMemo(
    () => safe(registrosFiltrados)
      .filter((x) => normalizeType(x.type) === 'despesa')
      .reduce((acc, x) => acc + extractValue(x), 0),
    [registrosFiltrados]
  );

  const saldo = useMemo(
    () => totalReceitas - totalDespesas,
    [totalReceitas, totalDespesas]
  );

  // ── Stats por categoria (ordenado por total) ──────────────────────────────
  const statsPorCategoria = useMemo(() => {
    const cats = {};
    safe(registrosFiltrados).forEach((item) => {
      const cat = item.category || "Outros";
      if (!cats[cat]) {
        cats[cat] = { 
          total: 0, 
          quantidade: 0, 
          type: normalizeType(item.type) || item.type || 'outros',
          categoria: cat
        };
      }
      cats[cat].total += extractValue(item);
      cats[cat].quantidade += 1;
    });
    
    return Object.values(cats).sort((a, b) => b.total - a.total);
  }, [registrosFiltrados]);

  // ── Tendência vs mês anterior (com proteção total) ───────────────────────
  const tendencia = useMemo(() => {
    if (!mes || typeof mes !== 'string' || !/^\d{4}-\d{2}$/.test(mes)) {
      return { valor: 0, percentual: 0, direcao: 'stable' };
    }

    const [ano, mesNum] = mes.split("-").map(Number);
    
    if (isNaN(ano) || isNaN(mesNum) || mesNum < 1 || mesNum > 12) {
      return { valor: 0, percentual: 0, direcao: 'stable' };
    }

    let mesAnterior = mesNum - 1;
    let anoAnterior = ano;
    if (mesAnterior === 0) { 
      mesAnterior = 12; 
      anoAnterior = ano - 1; 
    }
    
    const mesAnteriorStr = `${anoAnterior}-${String(mesAnterior).padStart(2, "0")}`;

    const registrosMesAnterior = r.filter(
      (x) => extractYearMonth(x?.date || x?.createdAt || x?.data) === mesAnteriorStr
    );
    
    // 🔥 V6: safeNum en cada extractValue
    const totalMesAnterior = registrosMesAnterior
      .filter((x) => normalizeType(x.type) === 'receita')
      .reduce((acc, x) => acc + safeNum(extractValue(x)), 0);

    const variacao = totalReceitas - totalMesAnterior;
    
    // 🔥 V6: Cálculo de percentual ULTRA SEGURO
    let percentual;
    if (totalMesAnterior > 0) {
      percentual = (variacao / totalMesAnterior) * 100;
    } else if (totalReceitas > 0) {
      percentual = 100;
    } else {
      percentual = 0;
    }

    // 🔥 V6: Protección final contra NaN y Infinity
    if (!isFinite(percentual)) {
      percentual = totalReceitas > 0 ? 100 : 0;
    }

    return {
      valor:      variacao,
      percentual: safePercent(percentual),
      direcao:    variacao > 0 ? "up" : variacao < 0 ? "down" : "stable",
    };
  }, [r, mes, totalReceitas]);

  // ── 🆕 V6: Resumo mensal (útil para dashboards) ──────────────────────────
  const resumoMensal = useMemo(() => {
    const receitasFiltradas = safe(registrosFiltrados).filter(x => normalizeType(x.type) === 'receita');
    
    // 🔥 V6: ticketMedio con safeDivide — NUNCA NaN
    const ticketMedio = safeDivide(totalReceitas, receitasFiltradas.length, 0);

    return {
      mes,
      totalReceitas,
      totalDespesas,
      saldo,
      quantidadeRegistros: registrosFiltrados.length,
      vendas: vendasStats.mes,
      alugueis: alugueisStats.mes,
      ticketMedio,
    };
  }, [mes, totalReceitas, totalDespesas, saldo, registrosFiltrados, vendasStats, alugueisStats]);

  // ── 🆕 V6: Totais gerais (todos os meses) ────────────────────────────────
  const totaisGerais = useMemo(() => {
    const todasReceitas = r
      .filter((x) => normalizeType(x.type) === 'receita')
      .reduce((acc, x) => acc + safeNum(extractValue(x)), 0);
    
    const todasDespesas = r
      .filter((x) => normalizeType(x.type) === 'despesa')
      .reduce((acc, x) => acc + safeNum(extractValue(x)), 0);

    return {
      totalReceitas: todasReceitas,
      totalDespesas: todasDespesas,
      saldoGeral: todasReceitas - todasDespesas,
      totalRegistros: r.length,
    };
  }, [r]);

  // ══════════════════════════════════════════════════════════════════════════
  // 🆕 V7: SPARKLINES — Mini gráficos diarios (6 días) estilo Vercel Analytics
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * 🔥 V7: Sparkline de Receitas — últimos 6 días con ingresos
   */
  const receitasSparkline = useMemo(
    () => computeDailySparkline(registrosFiltrados, 'receita', mes, 6),
    [registrosFiltrados, mes]
  );

  /**
   * 🔥 V7: Sparkline de Despesas — últimos 6 días con gastos
   */
  const despesasSparkline = useMemo(
    () => computeDailySparkline(registrosFiltrados, 'despesa', mes, 6),
    [registrosFiltrados, mes]
  );

  /**
   * 🔥 V7: Sparkline de Saldo — diferencia diaria (receitas - despesas)
   * Calculado día por día para mostrar fluctuación real del saldo
   */
  const saldoSparkline = useMemo(() => {
    const receitasDaily = computeDailySparkline(registrosFiltrados, 'receita', mes, 6);
    const despesasDaily = computeDailySparkline(registrosFiltrados, 'despesa', mes, 6);
    
    // Saldo día por día = receitas - despesas
    return receitasDaily.map((rec, i) => safeNum(rec) - safeNum(despesasDaily[i]));
  }, [registrosFiltrados, mes]);

  /**
   * 🔥 V7: Sparkline de Vendas — últimos 6 días con ventas
   */
  const vendasSparkline = useMemo(
    () => computeVendasSparkline(r, mes, 6),
    [r, mes]
  );

  /**
   * 🔥 V7: Sparkline de Aluguéis — últimos 6 días con alquileres
   */
  const alugueisSparkline = useMemo(
    () => computeAlugueisSparkline(r, mes, 6),
    [r, mes]
  );

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    temRegistrosNoMes,
    registrosFiltrados,
    vendasStats,
    alugueisStats,
    totalReceitas,
    totalDespesas,
    saldo,
    statsPorCategoria,
    tendencia,
    resumoMensal,
    totaisGerais,
    // 🆕 V7: Sparklines para SummaryCards
    receitasSparkline,
    despesasSparkline,
    saldoSparkline,
    vendasSparkline,
    alugueisSparkline,
  };
};

export default useFinanceStats;